import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Building2,
  Newspaper,
  Image as ImageIcon,
  Mail,
  Users,
  Settings,
  Layers,
  Menu as MenuIcon,
  X,
  LogOut,
  Home,
  Briefcase,
  UserSquare2,
  Split,
  Network,
  Quote,
  PhoneCall,
  Search,
  MapPin,
  Rocket,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import BrandLogo from "../common/BrandLogo";

const NAV = [
  { to: "/admin/dashboard", key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/homepage", key: "homepage", label: "Ana Sayfa", icon: Home },
  { to: "/admin/pages", key: "pages", label: "Sayfalar", icon: FileText },
  { to: "/admin/menus", key: "menus", label: "Menüler", icon: Layers },
  { to: "/admin/business-units", key: "business-units", label: "Faaliyet Alanları", icon: Building2 },
  { to: "/admin/group-companies", key: "group-companies", label: "Grup Şirketleri", icon: Network },
  { to: "/admin/team", key: "team", label: "Ekibimiz", icon: Users },
  { to: "/admin/projects", key: "projects", label: "Projeler", icon: Briefcase },
  { to: "/admin/yenisehir-projects", key: "yenisehir-projects", label: "Yenişehir Projeleri", icon: MapPin },
  { to: "/admin/landing-pages", key: "landing-pages", label: "Landing Sayfaları", icon: Rocket },
  { to: "/admin/nav-menu", key: "nav-menu", label: "Site Menüsü", icon: MenuIcon },
  { to: "/admin/news", key: "news", label: "Haberler", icon: Newspaper },
  { to: "/admin/testimonials", key: "testimonials", label: "Referanslar", icon: Quote },
  { to: "/admin/media", key: "media", label: "Medya Kütüphanesi", icon: ImageIcon },
  { to: "/admin/contact-submissions", key: "contact-submissions", label: "İletişim Formları", icon: Mail },
  { to: "/admin/callbacks", key: "callbacks", label: "Geri Arama Talepleri", icon: PhoneCall },
  { to: "/admin/career-openings", key: "career-openings", label: "Kariyer İlanları", icon: UserSquare2 },
  { to: "/admin/redirects", key: "redirects", label: "Yönlendirmeler", icon: Split },
  { to: "/admin/seo-analysis", key: "seo-analysis", label: "SEO Analiz Panosu", icon: Search },
  { to: "/admin/logos", key: "logos", label: "Logolar", icon: ImageIcon },
  { to: "/admin/settings", key: "settings", label: "Site Ayarları", icon: Settings },
  { to: "/admin/users", key: "users", label: "Kullanıcılar", icon: Users },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="admin-body flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-brand-navy text-white transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <BrandLogo place="header" onDark max={44} testId="admin-sidebar-logo" />
          <button type="button" className="lg:hidden text-white/80" onClick={() => setMobileOpen(false)} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto max-h-[calc(100vh-220px)]">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.key}
                to={n.to}
                data-testid={`admin-nav-${n.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "bg-white/5 border-brand-gold text-white"
                      : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 p-4 bg-brand-navy">
          <div className="text-[0.7rem] tracking-[0.24em] uppercase text-white/50 leading-relaxed">Signed in</div>
          <div className="text-sm text-white/90 mt-1 truncate" data-testid="admin-user-email">{user?.email}</div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-white/25 text-xs uppercase tracking-[0.22em] py-2 hover:bg-brand-gold hover:border-brand-gold transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-black/10 flex items-center justify-between px-4 h-14">
          <button type="button" onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="admin-menu-toggle">
            <MenuIcon size={20} />
          </button>
          <span className="font-serif text-brand-navy">Admin</span>
          <span className="w-6" />
        </div>
        <main className="p-6 md:p-10 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
